/**
 * The one HTTP client.
 *
 * Three things it does that the legacy frontend did not:
 *
 * 1. **Never stores a token.** Auth is httpOnly cookies, so a cross-site
 *    script cannot read a credential that works everywhere.
 * 2. **Refreshes once, transparently.** A 401 triggers a single refresh and a
 *    replay; concurrent 401s share that one refresh rather than stampeding.
 * 3. **Surfaces the request id.** Every error carries the id the server
 *    logged it under, so "quote this number to support" actually works.
 */

import type { SessionInfo } from '@/lib/types'

const BASE = '/api/v1'

export class ApiError extends Error {
  constructor(
    public status: number,
    public title: string,
    message: string,
    public requestId?: string,
    public fieldErrors?: { field: string; message: string }[],
    public payload?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when retrying could plausibly succeed - including a timeout (0). */
  get isTransient() {
    return this.status === 0 || this.status >= 500 || this.status === 429
  }
}

type Json = Record<string, unknown>

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: Json | FormData | null
  query?: Record<string, string | number | boolean | null | undefined>
  /** Skip the automatic refresh-and-retry. Used by the refresh call itself. */
  noRetry?: boolean
  idempotencyKey?: string
  /**
   * Give up after this long. Without it a request on a dropped connection
   * can hang for minutes - which on the tablet meant a guest looking at a
   * "Submitting" button that never came back. A timeout rejects with an
   * `ApiError` of status 0, which callers treat like any network failure.
   */
  timeoutMs?: number
}

/** Device token for the kiosk. Kept in memory; IndexedDB holds the durable copy. */
let deviceToken: string | null = null

export function setDeviceToken(token: string | null) {
  deviceToken = token
}

let refreshInFlight: Promise<boolean> | null = null

let refreshListeners: ((session: SessionInfo) => void)[] = []

/**
 * Hear about every refreshed session. The refresh happens down here, behind
 * a 401, so without this the signed-in state never learns what it returned -
 * including when it came back without the property the desk was working in.
 */
export function onSessionRefreshed(fn: (session: SessionInfo) => void): () => void {
  refreshListeners.push(fn)
  return () => {
    refreshListeners = refreshListeners.filter((f) => f !== fn)
  }
}

let lostListeners: (() => void)[] = []

/**
 * Hear when the sign-in is gone for good: a refresh the server refused.
 * Every query used to fail on its own with "Please sign in again" while the
 * page went on looking signed in - which is how a person signed out
 * everywhere appeared to still be signed in on their other PCs.
 */
export function onSessionLost(fn: () => void): () => void {
  lostListeners.push(fn)
  return () => {
    lostListeners = lostListeners.filter((f) => f !== fn)
  }
}

async function refreshSession(): Promise<boolean> {
  // One refresh at a time. Four parallel 401s must not become four refreshes,
  // because each rotates the token and the losers would then be replaying a
  // revoked one — which the server correctly treats as theft.
  refreshInFlight ??= (async () => {
    try {
      const send = () =>
        fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
      // And one at a time across tabs, which share the cookie. Two tabs whose
      // access tokens expired together used to refresh with the same token;
      // the second looked like theft and got "Not authenticated". Behind the
      // lock the second tab goes after the first, with the cookie it rotated
      // to. (No Web Locks on a plain-http LAN origin; the server's grace
      // window covers that case.)
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
      const response = locks ? await locks.request('ereg-auth-refresh', send) : await send()
      if (response.ok) {
        const session = (await response.json().catch(() => null)) as SessionInfo | null
        if (session) refreshListeners.forEach((fn) => fn(session))
      } else if (response.status === 401) {
        // Refused, not unreachable: a dropped network must not sign anyone out.
        lostListeners.forEach((fn) => fn())
      }
      return response.ok
    } catch {
      return false
    } finally {
      // Release on the next tick so simultaneous callers all see this result.
      setTimeout(() => {
        refreshInFlight = null
      }, 0)
    }
  })()
  return refreshInFlight
}

function buildUrl(path: string, query?: RequestOptions['query']) {
  const url = path.startsWith('http') ? path : `${BASE}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function toError(response: Response): Promise<ApiError> {
  let body: Json = {}
  try {
    body = (await response.json()) as Json
  } catch {
    /* an empty or non-JSON error body is still an error */
  }
  const detail =
    (body.detail as string) ??
    (body.title as string) ??
    response.statusText ??
    'Something went wrong'
  return new ApiError(
    response.status,
    (body.title as string) ?? 'Request failed',
    detail,
    (body.request_id as string) ?? response.headers.get('X-Request-ID') ?? undefined,
    body.errors as { field: string; message: string }[] | undefined,
    body,
  )
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, noRetry, idempotencyKey, timeoutMs, ...init } = options

  const headers = new Headers(init.headers)
  // Paired with SameSite cookies, this header is the CSRF guard: a
  // cross-origin form post cannot set it.
  headers.set('X-Requested-With', 'XMLHttpRequest')
  if (deviceToken) headers.set('Authorization', `Device ${deviceToken}`)
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey)

  let payload: BodyInit | undefined
  if (body instanceof FormData) {
    payload = body // let the browser set the multipart boundary
  } else if (body != null) {
    headers.set('Content-Type', 'application/json')
    payload = JSON.stringify(body)
  }

  const send = async () => {
    const controller = timeoutMs ? new AbortController() : null
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null
    try {
      return await fetch(buildUrl(path, query), {
        ...init,
        headers,
        body: payload,
        credentials: 'include',
        signal: controller?.signal ?? init.signal,
      })
    } catch (error) {
      if (controller?.signal.aborted) {
        throw new ApiError(0, 'Timed out', 'The connection timed out. Please try again.')
      }
      throw error
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  let response = await send()

  if (response.status === 401 && !noRetry && !deviceToken) {
    if (await refreshSession()) {
      response = await send()
    }
  }

  if (!response.ok) throw await toError(response)
  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/json')) return (await response.json()) as T
  return (await response.blob()) as unknown as T
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'GET', query }),

  post: <T>(path: string, body?: Json | FormData, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: Json) => request<T>(path, { method: 'PATCH', body }),

  put: <T>(path: string, body?: Json) => request<T>(path, { method: 'PUT', body }),

  delete: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'DELETE', query }),
}

/** Paginated list envelope — the same shape from every list endpoint. */
export interface Page<T> {
  items: T[]
  page: number
  size: number
  total: number
  pages: number
}
